import React, { useMemo, useState } from "react";
import Auth from "../Auth";
import { useToast } from "../components/ToastWrapper";
import { SudoButton } from "../sudokit/components/SudoButton";
import { Text } from "../sudokit/components/Text";

interface SplitSlice {
  index: number;
  title: string;
  targetDocId: string | null;
  commentIds: string[];
  blockRange: { start: number; end: number };
  annotationCount: number;
}

interface SplitPlan {
  sourceDocId: string;
  sourceDocTitle: string;
  userId: string;
  projectId: string;
  totalBlocks: number;
  totalComments: number;
  totalCommentsAssigned: number;
  totalAnnotations: number;
  targetPerSlice: number;
  slices: SplitSlice[];
  orphanedComments: Array<{ id: string; annotation_id: string; reason: string }>;
  unanchoredAssignments: Array<{ id: string; annotation_id: string; blockIndex: number; reason: string }>;
  crossBoundaryAnnotations: string[];
  threadsMoved: Array<{ parentId: string; replyIds: string[]; targetSlice: number }>;
}

interface ApplyResult extends SplitPlan {
  archivedTitle?: string;
  createdDocumentIds?: string[];
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-app-menu-color-hover bg-app-menu-color px-4 py-3">
      <div className="text-xs font-medium uppercase text-app-grey-1">{label}</div>
      <div className="mt-1 text-xl font-semibold text-app-grey-0">{value}</div>
    </div>
  );
}

function WarningList({ title, items }: { title: string; items: string[] }) {
  if (items.length === 0) return null;
  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
      <div className="font-semibold">{title}</div>
      <ul className="mt-2 list-disc pl-5">
        {items.slice(0, 8).map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
      {items.length > 8 && <div className="mt-2 text-xs">{items.length - 8} more</div>}
    </div>
  );
}

export default function FeedbackDocumentSplit() {
  const [documentId, setDocumentId] = useState("");
  const [targetPerSlice, setTargetPerSlice] = useState(250);
  const [confirmation, setConfirmation] = useState("");
  const [plan, setPlan] = useState<SplitPlan | null>(null);
  const [result, setResult] = useState<ApplyResult | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { setToast } = useToast();

  const trimmedDocumentId = documentId.trim();
  const expectedConfirmation = trimmedDocumentId ? `SPLIT ${trimmedDocumentId}` : "";
  const hasBlockingWarnings = !!plan && (plan.orphanedComments.length > 0 || plan.crossBoundaryAnnotations.length > 0);
  const canApply = !!plan && !hasBlockingWarnings && confirmation === expectedConfirmation && !applying && !loadingPreview;

  const unanchoredSummary = useMemo(() => {
    if (!plan) return "0 estimated";
    return `${plan.unanchoredAssignments.length} estimated`;
  }, [plan]);

  const previewSplit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!trimmedDocumentId || loadingPreview) return;

    setLoadingPreview(true);
    setError(null);
    setResult(null);
    setPlan(null);
    setConfirmation("");

    try {
      const response = await Auth.fetchWithIdToken("/api/admin/feedback-document-split/preview", {
        documentId: trimmedDocumentId,
        targetPerSlice,
      });
      if (!response?.success || !response.plan) {
        throw new Error(response?.message || response?.error || "Preview failed");
      }
      setPlan(response.plan);
    } catch (err) {
      const message = (err as Error)?.message || "Preview failed";
      setError(message);
      setToast(message);
    } finally {
      setLoadingPreview(false);
    }
  };

  const applySplit = async () => {
    if (!canApply || !plan) return;

    setApplying(true);
    setError(null);
    try {
      const response = await Auth.fetchWithIdToken("/api/admin/feedback-document-split/apply", {
        documentId: trimmedDocumentId,
        targetPerSlice,
        confirmation,
      });
      if (!response?.success || !response.result) {
        throw new Error(response?.message || response?.error || "Apply failed");
      }
      setResult(response.result);
      setToast(`Created ${response.result.createdDocumentIds?.length || 0} documents`);
    } catch (err) {
      const message = (err as Error)?.message || "Apply failed";
      setError(message);
      setToast(message);
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
      <div className="mb-6">
        <Text className="text-3xl font-semibold text-app-grey-0">Feedback Document Split</Text>
      </div>

      <form
        onSubmit={previewSplit}
        className="mb-6 rounded-lg border border-app-menu-color-hover bg-app-menu-color p-4"
      >
        <div className="grid gap-4 md:grid-cols-[minmax(240px,1fr)_220px_auto] md:items-end">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-app-grey-1">Document ID</span>
            <input
              className="h-10 w-full rounded border border-app-grey-2 bg-app-document-background px-3 text-sm text-app-grey-0"
              value={documentId}
              onChange={(event) => setDocumentId(event.target.value)}
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-sm font-medium text-app-grey-1">Target comments per split</span>
            <input
              className="h-10 w-full rounded border border-app-grey-2 bg-app-document-background px-3 text-sm text-app-grey-0"
              type="number"
              min={1}
              value={targetPerSlice}
              onChange={(event) => setTargetPerSlice(Math.max(1, Number(event.target.value) || 1))}
            />
          </label>

          <SudoButton
            type="submit"
            disabled={!trimmedDocumentId || loadingPreview}
            variant="primary"
          >
            {loadingPreview ? "Previewing..." : "Preview Split"}
          </SudoButton>
        </div>
      </form>

      {error && (
        <div className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">
          {error}
        </div>
      )}

      {plan && (
        <div className="space-y-4">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
            <Stat
              label="Comments"
              value={plan.totalComments}
            />
            <Stat
              label="Assigned"
              value={`${plan.totalCommentsAssigned} assigned`}
            />
            <Stat
              label="Blocks"
              value={plan.totalBlocks}
            />
            <Stat
              label="Parts"
              value={plan.slices.length}
            />
            <Stat
              label="Unanchored"
              value={unanchoredSummary}
            />
          </div>

          <div className="grid gap-3 lg:grid-cols-2">
            <WarningList
              title="Orphaned comments"
              items={plan.orphanedComments.map((comment) => `${comment.id} (${comment.annotation_id})`)}
            />
            <WarningList
              title="Cross-boundary annotations"
              items={plan.crossBoundaryAnnotations}
            />
          </div>

          <div className="overflow-auto rounded-lg border border-app-menu-color-hover bg-app-menu-color">
            <table className="min-w-full divide-y divide-app-menu-color-hover text-sm">
              <thead>
                <tr className="text-left text-xs font-semibold uppercase text-app-grey-1">
                  <th className="px-4 py-3">Part</th>
                  <th className="px-4 py-3">Comments</th>
                  <th className="px-4 py-3">Blocks</th>
                  <th className="px-4 py-3">Annotations</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-app-menu-color-hover text-app-grey-0">
                {plan.slices.map((slice) => (
                  <tr key={slice.index}>
                    <td className="px-4 py-3 font-medium">{slice.title}</td>
                    <td className="px-4 py-3">{slice.commentIds.length}</td>
                    <td className="px-4 py-3">
                      {slice.blockRange.start + 1}-{slice.blockRange.end + 1}
                    </td>
                    <td className="px-4 py-3">{slice.annotationCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="rounded-lg border border-app-menu-color-hover bg-app-menu-color p-4">
            <div className="grid gap-4 md:grid-cols-[minmax(240px,1fr)_auto] md:items-end">
              <label className="block">
                <span className="mb-1 block text-sm font-medium text-app-grey-1">Confirmation</span>
                <input
                  className="h-10 w-full rounded border border-app-grey-2 bg-app-document-background px-3 text-sm text-app-grey-0"
                  value={confirmation}
                  placeholder={expectedConfirmation}
                  onChange={(event) => setConfirmation(event.target.value)}
                />
              </label>

              <SudoButton
                type="button"
                disabled={!canApply}
                variant="primary"
                onClick={applySplit}
              >
                {applying ? "Applying..." : "Apply Split"}
              </SudoButton>
            </div>

            {hasBlockingWarnings && (
              <div className="mt-3 text-sm text-amber-900">
                Resolve blocking warnings before applying this split.
              </div>
            )}

            {result?.createdDocumentIds && (
              <div className="mt-4 rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <div className="font-semibold">Created {result.createdDocumentIds.length} documents</div>
                <div className="mt-1 break-all">{result.createdDocumentIds.join(", ")}</div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
